import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Modal } from "../../components/modal";
import {
  OrganizationForm,
  OrganizationFormValues,
} from "../../components/organization-form";
import { useAdditionalFields } from "../../hooks/addition-fields";
import { useGetOrganization } from "../../queries/organization";
import {
  parseAdditionalFieldToFormValue,
  parseFormValuesToAdditionalFields,
} from "../../services/additional-fields";
import { updateOrganization } from "../../services/organization";
import { navigateBack } from "../../utils/navigate-back";

export function OrganizationEdit() {
  const navigate = useNavigate();
  const { id } = useParams();

  if (!id) {
    throw new Error("Organization ID is required");
  }

  const { data: organization, isLoading: isLoadingOrganization } =
    useGetOrganization(id);

  const { data: additionalFields } = useAdditionalFields("organization");
  const queryClient = useQueryClient();

  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const [formValues, setFormValues] = useState<OrganizationFormValues>({
    corporateRegistrationNumber: "",
    name: "",
    email: "",
    phone: "",
    additionalFields: additionalFields.reduce(
      (acc, field) => ({
        ...acc,
        [field.name]: "",
      }),
      {},
    ),
  });

  const isLoading = isLoadingOrganization || loading;

  useEffect(() => {
    if (organization) {
      setFormValues({
        corporateRegistrationNumber: organization.corporateRegistrationNumber,
        name: organization.name ?? "",
        email: organization.email ?? "",
        phone: organization.phone ?? "",
        additionalFields: additionalFields.reduce(
          (acc, field) => ({
            ...acc,
            [field.name]: parseAdditionalFieldToFormValue(
              organization?.additionalData?.[field.name],
            ),
          }),
          {},
        ),
      });
    }
  }, [organization, additionalFields]);

  const closeModal = () => {
    navigateBack(navigate, "/admin/register/organization");
  };

  return (
    <Modal open onClose={() => closeModal()}>
      <OrganizationForm
        hasError={hasError}
        loading={isLoading}
        values={formValues}
        onCancel={() => closeModal()}
        onChange={setFormValues}
        onSubmit={() => {
          (async () => {
            setLoading(true);
            try {
              await updateOrganization(id, {
                corporateRegistrationNumber:
                  formValues.corporateRegistrationNumber,
                ...(formValues.name && { name: formValues.name }),
                ...(formValues.email && { email: formValues.email }),
                ...(formValues.phone && { phone: formValues.phone }),
                ...(formValues.additionalFields && {
                  additionalData: parseFormValuesToAdditionalFields(
                    formValues.additionalFields,
                    additionalFields,
                  ),
                }),
              });
              Promise.all([
                queryClient.invalidateQueries({
                  queryKey: ["organization", id],
                }),
                queryClient.invalidateQueries({
                  queryKey: ["organizations"],
                }),
              ]);
              closeModal();
            } catch (error: unknown) {
              setHasError(true);
            } finally {
              setLoading(false);
            }
          })();
        }}
      />
    </Modal>
  );
}
